import { useCallback, useState } from 'react';

import { getStartAndEndTimestamps } from 'shared/helpers/helpers';

// Coarsest to finest. The API picks the bucket size from the width of the
// requested range, so narrowing the range is what buys a finer interval.
export const INTERVAL_LADDER = [
  'months',
  'weeks',
  'days',
  'hours',
  'minutes',
];

export const canDrillInterval = (intervalUnit) => {
  const position = INTERVAL_LADDER.indexOf(intervalUnit);

  return position > -1 && position < INTERVAL_LADDER.length - 1;
};

// Keeps a stack of the ranges we came from so drill-out can walk back up.
// onRangeChange performs the refetch and must return a promise; if it
// rejects, the stack is rolled back so it cannot disagree with the view.
export const useChartDrilldown = ({ onRangeChange }) => {
  const [stack, setStack] = useState([]);

  const drillInto = useCallback(
    async (timestamp, intervalUnit, currentRange) => {
      if (!timestamp || !canDrillInterval(intervalUnit)) {
        return;
      }

      const { start, end } = getStartAndEndTimestamps(timestamp, intervalUnit);

      setStack((previous) => [...previous, currentRange]);

      try {
        await onRangeChange({ startDate: start, endDate: end });
      } catch (error) {
        // The refetch failed, so the view never moved. Undo our own push
        // rather than restoring a snapshot, which could clobber a
        // concurrent one.
        setStack((previous) => previous.slice(0, -1));
      }
    },
    [onRangeChange],
  );

  const drillOut = useCallback(async () => {
    if (stack.length === 0) {
      return;
    }

    const restored = stack[stack.length - 1];

    setStack((previous) => previous.slice(0, -1));

    try {
      await onRangeChange(restored);
    } catch (error) {
      setStack((previous) => [...previous, restored]);
    }
  }, [stack, onRangeChange]);

  const reset = useCallback(() => setStack([]), []);

  return {
    drillInto,
    drillOut,
    reset,
    canDrillOut: stack.length > 0,
    depth: stack.length,
  };
};
